import { Signal, signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { useQuery } from '@tanstack/react-query';
import { find, forEach, isEmpty, isEqual, isString, keys, pick, reduce, size, times } from 'es-toolkit/compat';
import { useEffect, useTransition } from 'react';
import { useQueryParam } from 'use-query-params';

import { getChannelsColorPresets } from 'api/slideMultiplexColorPresets';
import { MAX_VIEWERS } from 'components/Procedure/SlidesViewer/constants';
import { SlideWithChannelAndResults } from 'components/Procedure/useSlideChannelsAndResults/utils';
import {
  getViewerIndexedUrlSetting,
  hasExplicitMultiViewerSettings,
  shouldAllowViewerUrlStateFallback,
  useViewerLayoutUrlState,
} from 'components/Procedure/viewerStateUrlFallback';
import { defaultLayerColor, defaultLayerColors } from 'components/theme/theme';
import { ChannelColorPresetOptions, ChannelsColorPreset } from 'interfaces/channelsPreset';
import { SlideChannel } from 'interfaces/slide';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { statelessUpdateUrlParams } from 'utils/statelessUpdateUrlParams';
import { useCurrentLabId } from 'utils/useCurrentLab';
import useStudy from 'utils/useStudy';

export interface SlideChannelColorSettings {
  color?: any;
  gamma?: number;
  opacity?: number;
  dirty?: boolean;
}

// { [slideId]: { [channelId]: Signal<SlideChannelColorSettings> } }
export const slidesChannelColorSettings: Array<
  Signal<Record<string, Record<string, Signal<SlideChannelColorSettings>>>>
> = times(MAX_VIEWERS, () => signal({}));

const slideChannelMarkerTypesByViewerSlide: Record<string, Record<string, string> | null | undefined> = {};

export const rememberSlideChannelMarkerTypes = (
  viewerIndex: number,
  slideId: string | undefined,
  channelMarkerTypes: Record<string, string> | null | undefined
) => {
  if (!slideId) {
    return;
  }
  slideChannelMarkerTypesByViewerSlide[`${viewerIndex}:${slideId}`] = channelMarkerTypes;
};

export const getRememberedSlideChannelMarkerTypes = (viewerIndex: number, slideId: string) =>
  slideChannelMarkerTypesByViewerSlide[`${viewerIndex}:${slideId}`];

export const urlVisualSettingsFromChannelColorSettings = ['color', 'opacity', 'gamma'];
export const visualSettingsFromChannelColorPreset = ['color', 'gamma'];

export const getChannelColorPresetSettingsByMarkerType = (
  markerType?: string,
  preset?: Record<string, ChannelColorPresetOptions>
): SlideChannelColorSettings | undefined =>
  markerType ? pick(preset?.[markerType], visualSettingsFromChannelColorPreset) : undefined;

export const clearChannelColorSettingsSignals = () => {
  forEach(slidesChannelColorSettings, (viewerSettings) => {
    viewerSettings.value = {};
  });
};

const getMostRecentSlideSettingsForViewer = <T,>(
  settingsBySlide: Record<string, T> | undefined,
  currentSlideId?: string
): T | undefined => {
  const previousSlideSettings = Object.entries(settingsBySlide || {}).filter(([slideId]) => slideId !== currentSlideId);
  return previousSlideSettings.length ? previousSlideSettings[previousSlideSettings.length - 1][1] : undefined;
};

export const getSlideChannelColorSettingsSignal = (viewerIndex: number, slideId: string, channelId: string) => {
  const viewerSlidesChannelsSettings = slidesChannelColorSettings[viewerIndex];
  if (!viewerSlidesChannelsSettings) {
    console.warn(`Invalid viewerIndex: ${viewerIndex}`);
    return;
  }

  return viewerSlidesChannelsSettings.value?.[slideId]?.[channelId];
};

export const useUpdateSlideChannelColorSettings = () => {
  useSignals();

  const [, startTransition] = useTransition();

  const [markerChannelColorSettings] = useQueryParam<{
    [markerType: string]: { [viewerIndex: number]: SlideChannelColorSettings };
  }>('markerChannelColorSettings', CompressedJsonParam);

  const updateSlideChannelColorSettings = (
    viewerIndex: number,
    slideId: string,
    channelId: string,
    settings: Partial<SlideChannelColorSettings>,
    markerType?: string
  ) => {
    const slideChannelsSettingsSignal = getSlideChannelColorSettingsSignal(viewerIndex, slideId, channelId);

    if (slideChannelsSettingsSignal) {
      const previousSettings = slideChannelsSettingsSignal?.value;
      const newSettings = { ...previousSettings, ...settings };

      if (!isEqual(previousSettings, newSettings)) {
        slideChannelsSettingsSignal.value = newSettings;

        if (markerType) {
          // Persist visual settings only; local dirty state is not URL-backed.
          startTransition(() => {
            const encodedUrlValue = CompressedJsonParam.encode({
              ...(markerChannelColorSettings || {}),
              [markerType]: {
                ...markerChannelColorSettings?.[markerType],
                [viewerIndex]: pick(newSettings, urlVisualSettingsFromChannelColorSettings),
              },
            });
            if (isString(encodedUrlValue)) {
              statelessUpdateUrlParams({ markerChannelColorSettings: encodedUrlValue });
            }
          });
        }
      }
    }
  };

  const handleOpacityChange = (
    viewerIndex: number,
    slideId: string,
    channelId: string,
    opacity: number,
    markerType?: string
  ) => {
    updateSlideChannelColorSettings(viewerIndex, slideId, channelId, { opacity }, markerType);
  };

  const handleColorChange = (
    viewerIndex: number,
    slideId: string,
    channelId: string,
    color: any,
    markerType?: string
  ) => {
    updateSlideChannelColorSettings(viewerIndex, slideId, channelId, { color, dirty: true }, markerType);
  };

  const handleGammaChange = (
    viewerIndex: number,
    slideId: string,
    channelId: string,
    gamma: number,
    markerType?: string
  ) => {
    updateSlideChannelColorSettings(viewerIndex, slideId, channelId, { gamma, dirty: true }, markerType);
  };

  const handleShowChange = (viewerIndex: number, slideId: string, channelId: string, markerType?: string) => {
    const viewerSlidesChannelsSettings = getSlideChannelColorSettingsSignal(viewerIndex, slideId, channelId);
    handleOpacityChange(
      viewerIndex,
      slideId,
      channelId,
      viewerSlidesChannelsSettings?.value.opacity === 0 ? 100 : 0,
      markerType
    );
  };

  return { handleOpacityChange, handleColorChange, handleGammaChange, handleShowChange };
};

// { [slideId]: { [channelId]: boolean } }
export const slidesChannelToggles = times(MAX_VIEWERS, () => signal<Record<string, Record<string, boolean>>>({}));

export const clearChannelTogglesSignals = () => {
  forEach(slidesChannelToggles, (viewerSettings) => {
    viewerSettings.value = {};
  });
};

export const useSelectChannel = () => {
  useSignals();

  const [, startTransition] = useTransition();

  const [markerChannelToggle] = useQueryParam<{
    [markerType: string]: { [viewerIndex: number]: boolean };
  }>('markerChannelToggle', CompressedJsonParam);

  const handleSelectChannel = (
    viewerIndex: number,
    slideId: string,
    channelId: string,
    show: boolean,
    markerType?: string
  ) => {
    const viewerSlidesChannelsToggles = slidesChannelToggles[viewerIndex];
    if (!viewerSlidesChannelsToggles) {
      console.warn(`Invalid viewerIndex: ${viewerIndex}`);
      return;
    }

    viewerSlidesChannelsToggles.value = {
      ...viewerSlidesChannelsToggles.value,
      [slideId]: { ...viewerSlidesChannelsToggles.value[slideId], [channelId]: show },
    };

    if (markerType) {
      // Update marker type settings in URL
      startTransition(() => {
        const encodedUrlValue = CompressedJsonParam.encode({
          ...(markerChannelToggle || {}),
          [markerType]: {
            ...markerChannelToggle?.[markerType],
            [viewerIndex]: show,
          },
        });
        if (isString(encodedUrlValue)) {
          statelessUpdateUrlParams({ markerChannelToggle: encodedUrlValue });
        }
      });
    }
  };

  return { handleSelectChannel };
};

export const computeDefaultSlideChannelColorSettings = (
  channelIndex: number,
  channel: SlideChannel | undefined,
  colorPreset: ChannelsColorPreset | undefined,
  isDnaChannelId: boolean
): SlideChannelColorSettings & { id: number } => {
  const color =
    channel?.color && CSS.supports('color', channel.color)
      ? channel.color
      : isDnaChannelId
      ? defaultLayerColor
      : defaultLayerColors[(channelIndex ?? 0) % size(defaultLayerColors)];

  return {
    id: channelIndex,
    color,
    gamma: 1,
    opacity: 100,
    ...getChannelColorPresetSettingsByMarkerType(channel?.markerType, colorPreset?.options || {}),
  };
};

// Add slide channel settings for new slides
export const useSlideChannelViewSettings = (slide: SlideWithChannelAndResults, dnaChannelId: string) => {
  useSignals();
  const slideId = slide?.id;
  const viewerIndex = slide.viewerIndex;
  rememberSlideChannelMarkerTypes(viewerIndex, slideId, slide?.channelMarkerTypes);

  const slideChannels = slide?.channels;

  const viewerSlidesChannelsSettings = slidesChannelColorSettings[viewerIndex];
  const viewerSlideChannelToggles = slidesChannelToggles[viewerIndex];
  if (!viewerSlidesChannelsSettings || !viewerSlideChannelToggles) {
    console.warn(`Invalid viewerIndex: ${viewerIndex}`);
  }

  const [markerChannelColorSettings] = useQueryParam<{
    [markerType: string]: { [viewerIndex: number]: SlideChannelColorSettings };
  }>('markerChannelColorSettings', CompressedJsonParam);

  const [markerChannelToggle] = useQueryParam<{
    [markerType: string]: { [viewerIndex: number]: boolean };
  }>('markerChannelToggle', CompressedJsonParam);

  const { selectedSlideIds, viewersStainPreferences } = useViewerLayoutUrlState();
  const shouldAllowColorSettingsFallback = shouldAllowViewerUrlStateFallback({
    hasExplicitMultiViewerState: hasExplicitMultiViewerSettings(markerChannelColorSettings),
    selectedSlideIds,
    viewersStainPreferences,
  });
  const shouldAllowChannelToggleFallback = shouldAllowViewerUrlStateFallback({
    hasExplicitMultiViewerState: hasExplicitMultiViewerSettings(markerChannelToggle),
    selectedSlideIds,
    viewersStainPreferences,
  });

  const { labId } = useCurrentLabId();
  const { data: channelPresets, isLoading: isLoadingChannelPresets } = useQuery<ChannelsColorPreset[]>(
    ['channelPresets', labId],
    () => getChannelsColorPresets(labId)
  );

  const { study, isLoading: isLoadingStudy } = useStudy(slide.studyId);
  const studyDefaultColorPresetId = study?.settings?.defaultMultiplexColorPreset;
  const studyDefaultColorPreset = find(channelPresets, (preset) => preset.id === studyDefaultColorPresetId);

  useEffect(() => {
    if (viewerSlidesChannelsSettings && slideId && !isEmpty(slideChannels)) {
      const previousViewerSlidesChannelsSettings = viewerSlidesChannelsSettings.value;
      const previousSlideChannelSettings = previousViewerSlidesChannelsSettings[slideId] || {};
      const previousViewerSlideSettingsForFallback = getMostRecentSlideSettingsForViewer(
        previousViewerSlidesChannelsSettings,
        slideId
      );

      const newSlideChannelSettings: Record<string, Signal<SlideChannelColorSettings>> = reduce(
        Object.entries(slide?.channels || {}),
        (acc, [channelIndex, channel]) => {
          const markerType = slide?.channelMarkerTypes?.[channelIndex];
          const explicitColorSettingsFromUrl = markerType
            ? markerChannelColorSettings?.[markerType]?.[viewerIndex]
            : undefined;
          const fallbackColorSettingsFromUrl =
            markerType && !explicitColorSettingsFromUrl
              ? getViewerIndexedUrlSetting({
                  settingsByViewer: markerChannelColorSettings?.[markerType],
                  viewerIndex,
                  shouldAllowFallback: shouldAllowColorSettingsFallback,
                })
              : undefined;
          const previousViewerChannelSettings = previousSlideChannelSettings[channelIndex]
            ? undefined
            : previousViewerSlideSettingsForFallback?.[channelIndex]?.value;
          const explicitSelectedFromUrl = markerType ? markerChannelToggle?.[markerType]?.[viewerIndex] : undefined;
          const fallbackSelectedFromUrl =
            markerType && explicitSelectedFromUrl === undefined
              ? getViewerIndexedUrlSetting({
                  settingsByViewer: markerChannelToggle?.[markerType],
                  viewerIndex,
                  shouldAllowFallback: shouldAllowChannelToggleFallback,
                })
              : undefined;
          const selectedFromUrl = explicitSelectedFromUrl ?? fallbackSelectedFromUrl;
          const resolvedUrlColorSettings = explicitColorSettingsFromUrl || fallbackColorSettingsFromUrl;

          const newColorSettings = {
            ...computeDefaultSlideChannelColorSettings(
              Number(channelIndex),
              channel,
              studyDefaultColorPreset,
              channelIndex === dnaChannelId
            ),
            ...(fallbackColorSettingsFromUrl || {}),
            ...(previousViewerChannelSettings || {}),
            ...(explicitColorSettingsFromUrl || {}),
            ...(selectedFromUrl && !previousViewerChannelSettings && resolvedUrlColorSettings?.opacity === 0
              ? { opacity: 100 }
              : {}),
          };
          if (acc?.[channelIndex] === undefined) {
            // Creating default channel settings for slide
            acc[channelIndex] = signal(newColorSettings);
          } else {
            // Updating existing channel settings
            const previousChannelSettings = acc[channelIndex].value;
            // Only seed channel state from the URL when the slide is first loaded.
            acc[channelIndex].value = { ...newColorSettings, ...previousChannelSettings };
          }
          return acc;
        },
        previousSlideChannelSettings
      );

      viewerSlidesChannelsSettings.value = {
        ...previousViewerSlidesChannelsSettings,
        [slideId]: newSlideChannelSettings,
      };
    }
  }, [
    viewerSlidesChannelsSettings,
    viewerIndex,
    slideId,
    slideChannels,
    markerChannelColorSettings,
    markerChannelToggle,
    shouldAllowColorSettingsFallback,
    shouldAllowChannelToggleFallback,
    studyDefaultColorPreset,
  ]);

  useEffect(() => {
    if (viewerSlideChannelToggles && slideId && !isEmpty(slideChannels)) {
      const previousViewerSlideChannelToggles = viewerSlideChannelToggles.value;
      const previousViewerSlideTogglesForFallback = getMostRecentSlideSettingsForViewer(
        previousViewerSlideChannelToggles,
        slideId
      );
      viewerSlideChannelToggles.value = {
        ...previousViewerSlideChannelToggles,
        [slideId]: {
          ...(previousViewerSlideChannelToggles?.[slideId] || {}),
          ...reduce(
            keys(slide?.channels),
            (acc, channelIndex) => {
              const markerType = slide?.channelMarkerTypes?.[channelIndex];
              const explicitShowFromUrl = markerType ? markerChannelToggle?.[markerType]?.[viewerIndex] : undefined;
              const fallbackShowFromUrl =
                markerType && explicitShowFromUrl === undefined
                  ? getViewerIndexedUrlSetting({
                      settingsByViewer: markerChannelToggle?.[markerType],
                      viewerIndex,
                      shouldAllowFallback: shouldAllowChannelToggleFallback,
                    })
                  : undefined;
              const previousViewerChannelToggle = previousViewerSlideChannelToggles?.[slideId]?.[channelIndex]
                ? undefined
                : previousViewerSlideTogglesForFallback?.[channelIndex];
              const showFromUrl = explicitShowFromUrl ?? previousViewerChannelToggle ?? fallbackShowFromUrl;

              acc[channelIndex] = previousViewerSlideChannelToggles?.[slideId]?.[channelIndex] ?? showFromUrl;
              return acc;
            },
            {} as Record<string, boolean>
          ),
          [dnaChannelId]: true,
        },
      };
    }
  }, [
    viewerSlideChannelToggles,
    viewerIndex,
    slideId,
    slideChannels,
    markerChannelToggle,
    shouldAllowChannelToggleFallback,
  ]);

  return { isLoading: isLoadingChannelPresets || isLoadingStudy };
};
